/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, OnInit, signal} from '@angular/core';
import {UIRouter} from '@uirouter/core';
import * as _ from 'lodash';

import {OBJECT_TYPE} from '../../common/constants/object-type';
import {AdnShotterService} from '../../common/adn-shotter.service';
import {buildLastViewedEntries} from './simple-last-viewed.util';
import template from './preview-shotter-page.component.html';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {deferSignalViewWrite} from '../../common/util/template-state';
import {NavigationHistoryService} from '../../../components/util/navigation-history.service';
import {visiblePageNumbers} from '../../common/directives/pagination/visible-page-numbers';

@Component({
  standalone: false,
  selector: 'adn-preview-shotter-page',
  template
})
export class PreviewShotterPageComponent implements OnInit {
  lastViewed: any[] = [];
  lastViewedObject: any;
  selectFrom = 'CUSTOM';

  width = 300;
  height = 250;
  device = 'DESKTOP';
  screenshots: any[];
  screenshotting = false;
  deleting = false;
  showScreenshots = false;
  loaded = true;
  takingScreenshot = false;
  takeSuccess = false;
  takeError = '';
  objectId: string;
  objectType: any;
  objectTypes = OBJECT_TYPE;
  hashResults = true;
  resp: any;

  missingPreviewShots: any[];
  fetchingMissingPreview = false;
  showMissingPreview = false;
  triggeringMissingPreview: string;

  previewsByCreativeMeta = {
    currentPage: 1,
    pageSize: 20,
    resolved: true
  };
  previewsByCreativePage: any;
  fetchingPreviewsByCreative = false;
  showPreviewsByCreative = false;
  readonly viewRevision = signal(0);

  constructor(
    @Inject(LocalUserProfile) localUserProfile: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly adnShotter: AdnShotterService,
    private readonly uiRouter: UIRouter,
    navigationHistory: NavigationHistoryService
  ) {
    this.lastViewed = buildLastViewedEntries(localUserProfile, this.LocalNetworkProfile);
    this.lastViewedObject = _.get(this.lastViewed, [0]);
    this.objectId = navigationHistory.previousParams.id;
    this.objectType = _.find(_.values(OBJECT_TYPE), (ot) => ot.stateName === navigationHistory.previousState?.name);
  }

  ngOnInit(): void {
    const creativeId = this.uiRouter.globals.params.creativeId;
    if (creativeId) {
      this.objectId = creativeId;
      this.doCheck();
    }
  }

  refresh(): void {
    this.screenshotting = true;
    this.showScreenshots = true;
    this.adnShotter.fetchAllPreviews().then((data: any[]) => {
      // undefined → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferSignalViewWrite(this.viewRevision, () => {
        this.screenshots = data;
      });
    }).finally(() => {
      deferSignalViewWrite(this.viewRevision, () => this.screenshotting = false);
    });
  }

  refreshMissingPreview(): void {
    this.fetchingMissingPreview = true;
    this.showMissingPreview = true;
    this.adnShotter.missingPreview().then((data: any[]) => {
      // undefined → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferSignalViewWrite(this.viewRevision, () => {
        this.missingPreviewShots = data;
      });
    }).finally(() => {
      deferSignalViewWrite(this.viewRevision, () => this.fetchingMissingPreview = false);
    });
  }

  refreshPreviewsByCreative(): void {
    this.fetchPreviewsByCreative(this.previewsByCreativeMeta.currentPage);
  }

  changePreviewsByCreativePage(page: number): void {
    if (page < 1 || page > this.previewsByCreativeTotalPages() || page === this.previewsByCreativeMeta.currentPage) {
      return;
    }
    this.previewsByCreativeMeta.currentPage = page;
    this.fetchPreviewsByCreative(page);
  }

  previewsByCreativeTotalPages(): number {
    return Math.max(1, Math.ceil((this.previewsByCreativePage?.totalHits || 0) / this.previewsByCreativeMeta.pageSize));
  }

  previewsByCreativePageNumbers(): number[] {
    return visiblePageNumbers(this.previewsByCreativeMeta.currentPage, this.previewsByCreativeTotalPages());
  }

  deleteScreenshot(screenshot: any, index: number): void {
    this.deleting = true;
    this.adnShotter.deleteScreenshot(screenshot).then(() => {
      _.pullAt(this.screenshots, index);
    }).finally(() => {
      deferSignalViewWrite(this.viewRevision, () => this.deleting = false);
    });
  }

  doCheck(): void {
    if (!this.objectId) {
      this.takeSuccess = false;
      this.takeError = 'Enter a creative ID first.';
      return;
    }
    this.loaded = false;
    this.takingScreenshot = true;
    this.takeSuccess = false;
    this.takeError = '';
    this.resp = null;
    this.adnShotter.takePreview(this.objectId, this.device).then((data: any) => {
      this.resp = data;
      this.takeSuccess = true;
    }).catch((error: any) => {
      this.takeError = _.get(error, ['data', 'message'])
        || _.get(error, ['data'])
        || 'Unable to queue the preview screenshot.';
      if (!_.isString(this.takeError)) {
        this.takeError = 'Unable to queue the preview screenshot.';
      }
    }).finally(() => {
      deferSignalViewWrite(this.viewRevision, () => {
        this.loaded = true;
        this.takingScreenshot = false;
      });
    });
  }

  takeMissingPreview(creativeId: string): void {
    this.triggeringMissingPreview = creativeId;
    this.adnShotter.takePreview(creativeId, this.device).finally(() => {
      deferSignalViewWrite(this.viewRevision, () => {
        this.triggeringMissingPreview = null;
        this.refreshMissingPreview();
      });
    });
  }

  private fetchPreviewsByCreative(page?: number): void {
    this.fetchingPreviewsByCreative = true;
    this.showPreviewsByCreative = true;
    this.adnShotter.previewsByCreative(page || this.previewsByCreativeMeta.currentPage).then((data: any) => {
      // undefined → page flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferSignalViewWrite(this.viewRevision, () => {
        this.previewsByCreativeMeta.currentPage = data.page;
        this.previewsByCreativeMeta.pageSize = data.pageSize;
        this.previewsByCreativePage = {
          totalHits: data.totalCount,
          pageStart: data.totalCount ? ((data.page - 1) * data.pageSize) + 1 : 0,
          pageEnd: Math.min(data.totalCount, data.page * data.pageSize),
          items: _.map(data.results, (screenshots: any[], creativeId: string) => ({
            creativeId: creativeId,
            creative: _.get(screenshots, [0, 'creative']),
            lineItem: _.get(screenshots, [0, 'lineItem']),
            screenshots: screenshots
          }))
        };
      });
    }).finally(() => {
      deferSignalViewWrite(this.viewRevision, () => this.fetchingPreviewsByCreative = false);
    });
  }
}
